/* eslint-disable no-restricted-globals */
/* eslint-disable react-hooks/exhaustive-deps */
import DownloadIcon from '@material-ui/icons/GetApp';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import {
  ClientFields,
  HistorySessionFields,
  HistorySessionProps,
} from 'modules/clients/interfaces';
import { formatDateWithMoment } from 'utils/helpers';
import { Grid, makeStyles, IconButton } from '@material-ui/core';
import React, { useState } from 'react';
import ApplicationState from 'store/interfaces';
import SearchIcon from '@material-ui/icons/Search';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import {
  exportHistorySessionCSVRequest,
  getHistorySessionsRequest,
} from 'modules/clients/actions';
import DatePicker from 'components/DatePicker';
import { format } from 'date-fns';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import RefreshButton from 'components/RefreshButton';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import Button from 'components/Button';
import clsx from 'clsx';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Query {
  offset: number;
  limit: number;
  search: string;
  'start-datetime': string | null;
  'end-datetime': string | null;
}

const initialQuery: Query = {
  offset: 0,
  limit: 25,
  search: '',
  'start-datetime': null,
  'end-datetime': null,
};

interface SessionHistoryProps {
  historySessions: HistorySessionProps;
  isLoading: boolean;
  clientSelected: ClientFields | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  getHistorySessionsRequest: ({ id, query }) => void;
  exportHistorySessionCSVRequest: ({ id, query, filename }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const SessionHistory: React.FC<SessionHistoryProps> = ({
  historySessions,
  isLoading,
  clientSelected,
  companySelected,
  role,
  getHistorySessionsRequest,
  exportHistorySessionCSVRequest,
  modalOpen,
  modalClose,
}) => {
  const classes = useStyles();
  const {
    query,
    searchValue,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);
  const { id } = useParams<{ id: string }>();
  const [refresh, setRefresh] = React.useState(false);
  const [startDatetime, setStartDatetime] = useState<ParsableDate>(null);
  const [endDatetime, setEndDatetime] = useState<ParsableDate>(null);

  const headersSession = [
    {
      key: 'user__nome',
      i18n: 'USERS_KEY',
      order: true,
    },
    {
      key: 'user__email',
      i18n: 'EMAIL_KEY',
      order: true,
    },
    {
      key: 'creation_date',
      i18n: 'LOGIN_DATE_KEY',
      order: true,
      textAlignCenter: true,
      centerTitle: true,
    },
    {
      key: 'termination_date',
      i18n: 'LOGOUT_DATE_KEY',
      order: true,
      textAlignCenter: true,
      centerTitle: true,
    },
    {
      key: 'my_ip',
      i18n: 'IP_ORIGIN_KEY',
      order: true,
    },
    {
      key: 'ec2_public_ip',
      i18n: 'IP_DESTINY_KEY',
      order: true,
    },
    {
      key: 'termination_reason',
      i18n: 'TERMINATE_RATE_KEY',
      order: true,
    },
  ];

  const renderTerminate = {
    sessionremovaltimeout: 'CLIENTS_PAGE.SESSION_REMOVAL_TIMEOUT_KEY',
    expiredsession: 'CLIENTS_PAGE.EXPIRED_SESSION_HISTORY_KEY',
    closedsession: 'CLIENTS_PAGE.CLOSED_SESSION_HISTORY_KEY',
    instancedeleted: 'CLIENTS_PAGE.REMOVE_SESSION_HISTORY_KEY',
  };

  const formatDatetime = datetime => {
    return format(datetime, "yyyy-MM-dd'T'HH:mm");
  };

  const isInvalidDatetime = datetime =>
    datetime instanceof Date && isNaN(datetime.getTime());

  const handleSubmit = () => {
    setQuery(prev => ({
      ...prev,
      offset: 0,
      'start-datetime': startDatetime
        ? `${formatDatetime(startDatetime)}:00`
        : '',
      'end-datetime': endDatetime ? `${formatDatetime(endDatetime)}:59` : '',
    }));
  };

  const handleExportSessionHistoryAsCSV = () => {
    modalOpen({
      title: i18n.t('WANT_EXPORT_CSV_KEY'),
      content: i18n.t('EXPORT_CSV_DESCRIPTION_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              exportHistorySessionCSVRequest({
                id,
                query,
                filename: `${i18n.t('LIST_HISTORY_SESSION_EXPORT_FILENAME')}_${
                  clientSelected?.name
                }`,
              });
              modalClose();
            }}
          >
            {i18n.t('EXPORT_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const getSessionHistory = (refresh = false) => {
    getHistorySessionsRequest({ id, query });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getSessionHistory();
  }, [getHistorySessionsRequest, query]);

  return (
    <Grid>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12} className={classes.iconButton}>
          <RefreshButton
            id="btn-refresh"
            animate={isLoading}
            disabled={isLoading}
            onClick={() => getSessionHistory()}
          />
          <Button
            id="export-session-history-as-csv-button"
            variant="contained"
            color="inherit"
            size="small"
            className={clsx({
              [classes.exportButtonGreen]: inAdminView(role, companySelected),
              [classes.exportButtonGray]: inSuperAdminView(
                role,
                companySelected
              ),
              [classes.exportButtonBlue]: inOperatorView(role, companySelected),
              [classes.exportButton]: companySelected,
            })}
            startIcon={<DownloadIcon />}
            onClick={() => handleExportSessionHistoryAsCSV()}
            disabled={
              isInvalidDatetime(startDatetime) || isInvalidDatetime(endDatetime)
            }
          >
            {i18n.t('EXPORT_CSV_KEY')}
          </Button>
        </Grid>
        <Grid item xs={5}>
          <SearchBar
            id="search-sessions"
            isLoading={isLoading}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid item xs={3}>
          <DatePicker
            id="startDatetime"
            type="datetime"
            keyValue="startDatetime"
            setFieldsValue={(key, value) => {
              setStartDatetime(value);
            }}
            label={i18n.t('START_DATE_KEY')}
            value={startDatetime}
          />
        </Grid>
        <Grid item xs={3}>
          <DatePicker
            id="endDatetime"
            type="datetime"
            keyValue="endDatetime"
            setFieldsValue={(key, value) => {
              setEndDatetime(value);
            }}
            minDate={startDatetime}
            label={i18n.t('END_DATE_KEY')}
            value={endDatetime}
          />
        </Grid>
        <Grid item xs={1} className={classes.iconButton}>
          <IconButton
            onClick={handleSubmit}
            disabled={
              isInvalidDatetime(startDatetime) ||
              isInvalidDatetime(endDatetime) ||
              isLoading
            }
          >
            <SearchIcon />
          </IconButton>
        </Grid>
      </Grid>
      <TableItems
        header={
          <>
            {headersSession.map(item => (
              <TableCell
                className={classes.header}
                textAlignCenter={item?.textAlignCenter}
                centerTitle={item?.centerTitle}
                key={item?.i18n}
                handleOrder={() =>
                  item?.order ? handleOrder(item?.key) : null
                }
                order={item?.order}
              >
                {i18n.t(item?.i18n)}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: HistorySessionFields) => (
          <TableRow key={item?.my_ip}>
            <TableCell>{item?.user.full_name}</TableCell>
            <TableCell>{item?.user.email}</TableCell>
            <TableCell centerTitle>
              {formatDateWithMoment(item?.creation_date)}
            </TableCell>
            <TableCell centerTitle>
              {formatDateWithMoment(item?.termination_date)}
            </TableCell>
            <TableCell>{item?.my_ip}</TableCell>
            <TableCell>{item?.ec2_public_ip}</TableCell>
            <TableCell>
              {i18n.t(
                item?.termination_reason &&
                  renderTerminate[
                    item?.termination_reason.toLowerCase().replace(/ /g, '')
                  ]
              )}
            </TableCell>
          </TableRow>
        )}
        data={historySessions}
        isLoading={!refresh && isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={i18n.t('CLIENTS_PAGE.NO_SESSION_HISTORY_KEY')}
        overrideRowsCount={query?.limit}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  clients: { isLoading, listSessionClients, clientSelected, historySessions },
}: ApplicationState) => ({
  isLoading,
  listSessionClients,
  clientSelected,
  historySessions,
  companySelected,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getHistorySessionsRequest,
  exportHistorySessionCSVRequest,
  modalOpen,
  modalClose,
})(SessionHistory);
